/** Tenký klient nad REST API dashboardu. */

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    ...init,
  });
  const text = await response.text();
  const body: unknown = text === "" ? null : JSON.parse(text);
  if (!response.ok) {
    const detail =
      typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${response.status}`;
    throw new ApiError(detail, response.status);
  }
  return body as T;
}

export interface Signal {
  id: string;
  detected_at: string;
  valid_until: string;
  status: string;
  mode: "actionable" | "paper";
  league_name: string;
  home_team: string;
  away_team: string;
  starts_at: string;
  source_name: string;
  market_key: string;
  outcome_code: string;
  offered_odds: string;
  fair_prob: string;
  edge: string;
  suggested_stake: string;
  bet_id: string | null;
  matched_odds: string | null;
  clv_signal: string | null;
  clv_realized: string | null;
}

export interface ClvSummary {
  n: number;
  meanClvSignal: number | null;
  meanClvRealized: number | null;
  meanSlippage: number | null;
  positiveShare: number | null;
}

export interface ReviewItem {
  id: string;
  kind: string;
  raw_value: { rawName?: string; normalized?: string; reason?: string };
  candidates: { teamId: string; canonicalName: string; similarity: number }[];
  created_at: string;
  provider_code: string;
}

export interface OpsStatus {
  quota: { provider: string; bucket: string; credits_spent: number; monthly_credit_limit: number }[];
  units: {
    provider: string; league: string; market_code: string;
    last_captured_at: string | null; next_capture_at: string | null;
    consecutive_failures: number; enabled: boolean;
  }[];
  nextKickoff: string | null;
  upcomingEvents: number;
  openReviewItems: number;
  snapshotsLast24h: number;
}

export const api = {
  login: (password: string) =>
    request<{ ok: true }>("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  signals: (params: { status?: string; mode?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined),
    );
    return request<{ signals: Signal[] }>(`/api/signals?${query}`);
  },
  signal: (id: string) =>
    request<{ signal: Signal; history: { captured_at: string; source_code: string; price: string }[] }>(
      `/api/signals/${id}`,
    ),
  recordBet: (id: string, body: { matchedOdds: number; stake: number; note?: string }) =>
    request<{ betId: string }>(`/api/signals/${id}/bet`, { method: "POST", body: JSON.stringify(body) }),
  skip: (id: string, reason: string) =>
    request<{ ok: true }>(`/api/signals/${id}/skip`, { method: "POST", body: JSON.stringify({ reason }) }),
  clv: (mode?: string) =>
    request<{ summary: ClvSummary; values: number[] }>(`/api/clv${mode ? `?mode=${mode}` : ""}`),
  reviewQueue: () => request<{ items: ReviewItem[] }>("/api/review-queue"),
  resolveReview: (id: string, body: { teamId?: string; ignore?: boolean }) =>
    request<{ ok: true }>(`/api/review-queue/${id}/resolve`, { method: "POST", body: JSON.stringify(body) }),
  ops: () => request<OpsStatus>("/api/ops"),
};
